/// Polymorphic target shared by every sales_action API payload.
export type SalesActionTarget =
  | { kind: 'firm'; id: string }
  | { kind: 'lawyer'; id: string };

export type SalesStatus =
  | 'no_contact'
  | 'contacted'
  | 'in_dialogue'
  | 'appointment'
  | 'negotiation_c'
  | 'negotiation_b'
  | 'negotiation_a'
  | 'won'
  | 'lost';

export const SALES_STATUS_LABELS: Record<SalesStatus, string> = {
  no_contact: '未接点',
  contacted: '接点あり',
  in_dialogue: '対話あり',
  appointment: 'アポ予定',
  negotiation_c: '商談/C',
  negotiation_b: '商談/B',
  negotiation_a: '商談/A',
  won: '受注',
  lost: '失注',
};

export const SALES_STATUS_ORDER: SalesStatus[] = [
  'no_contact',
  'contacted',
  'in_dialogue',
  'appointment',
  'negotiation_c',
  'negotiation_b',
  'negotiation_a',
  'won',
  'lost',
];

export type ActionStatus = 'open' | 'completed' | 'canceled';

export const ACTION_STATUS_LABELS: Record<ActionStatus, string> = {
  open: '実行中',
  completed: '完了',
  canceled: '取消',
};

export type ActionType =
  | 'call'
  | 'direct_message'
  | 'email'
  | 'referral_request'
  | 'material_prep'
  | 'pre_hearing'
  | 'concern_response'
  | 'quote_pricing'
  | 'next_appointment'
  | 'final_adjustment'
  | 'contract_procedure';

export const ACTION_TYPE_LABELS: Record<ActionType, string> = {
  call: '架電',
  direct_message: 'DM',
  email: 'メール',
  referral_request: '紹介依頼',
  material_prep: '資料準備',
  pre_hearing: '事前ヒアリング',
  concern_response: '懸念対応',
  quote_pricing: '見積・価格調整',
  next_appointment: '次回アポ調整',
  final_adjustment: '最終調整',
  contract_procedure: '契約手続き',
};

export const ACTION_TYPE_ORDER: ActionType[] = [
  'call',
  'direct_message',
  'email',
  'referral_request',
  'material_prep',
  'pre_hearing',
  'concern_response',
  'quote_pricing',
  'next_appointment',
  'final_adjustment',
  'contract_procedure',
];

export type CompletionResult =
  | 'progressed'
  | 'no_change'
  | 'regressed'
  | 'on_hold'
  | 'lost'
  | 'next_contact_set'
  | 'no_response';

export const COMPLETION_RESULT_LABELS: Record<CompletionResult, string> = {
  progressed: '前進',
  no_change: '変化なし',
  regressed: '後退',
  on_hold: '保留',
  lost: '失注',
  next_contact_set: '次回約束',
  no_response: '反応なし',
};

export const COMPLETION_RESULT_ORDER: CompletionResult[] = [
  'progressed',
  'no_change',
  'regressed',
  'on_hold',
  'lost',
  'next_contact_set',
  'no_response',
];

export type LostReason =
  | 'budget_not_secured'
  | 'competitor_adopted'
  | 'too_early'
  | 'needs_mismatch'
  | 'internal_alignment_failed'
  | 'no_response'
  | 'other';

export const LOST_REASON_LABELS: Record<LostReason, string> = {
  budget_not_secured: '予算未確保',
  competitor_adopted: '競合採用',
  too_early: '時期尚早',
  needs_mismatch: 'ニーズ不一致',
  internal_alignment_failed: '社内調整不調',
  no_response: '反応途絶',
  other: 'その他',
};

export const LOST_REASON_ORDER: LostReason[] = [
  'budget_not_secured',
  'competitor_adopted',
  'too_early',
  'needs_mismatch',
  'internal_alignment_failed',
  'no_response',
  'other',
];

/**
 * 営業アクションが「要確認」になった理由 (Issue #524 PR2)。 保存値であって表示
 * ラベルではない。 `REVIEW_REQUIRED_REASON_LABELS` で行ラベルへ変換する。
 */
export type ReviewRequiredReason =
  | 'auto_added'
  | 'other_user_created'
  | 'other_user_updated'
  | 'important_change';

export const REVIEW_REQUIRED_REASON_LABELS: Record<ReviewRequiredReason, string> =
  {
    auto_added: '自動',
    other_user_created: '新規',
    other_user_updated: '更新',
    important_change: '要確認',
  };

export interface SalesAction {
  id: string;
  target: SalesActionTarget;
  /** 同席者 / 商談相手 (multi / catalog No.116)。 その事務所の所属弁護士 id。 */
  participant_lawyer_ids: string[];
  product_id: string | null;
  /** 商談前の候補ソリューション群 (商談以降の案件ソリューション product_id とは別保存値)。 空配列 = 候補なし。 */
  candidate_product_ids: string[];
  sales_status: SalesStatus;
  action_status: ActionStatus;
  action_type: ActionType | null;
  action_title: string | null;
  customer_action: string | null;
  success_condition: string | null;
  note: string | null;
  likelihood: string | null;
  due_date: string | null;
  issue_categories: unknown;
  completion_result: CompletionResult | null;
  completion_note: string | null;
  lost_reason: LostReason | null;
  lost_reason_note: string | null;
  completed_at: string | null;
  source_customer_problem_id: string | null;
  source_dialogue_log_id: string | null;
  linked_opportunity_id: string | null;
  linked_contract_id: string | null;
  /** 「ステータスを戻す」の戻し先 (`sales_status_events` からの導出値・表示専用)。
   * `null` = ボタン非表示。 */
  restore_target: SalesStatus | null;
  /** 作成 / 最終更新者 (CRM 担当者 `app_accounts.id`)。 自分の変更を更新表示から除外する (#578)。 */
  created_by_account_id: string | null;
  updated_by_account_id: string | null;
  /** 要確認の保存値。 表示は派生の `review_required` を使う。 */
  review_required_at: string | null;
  review_required_reason: ReviewRequiredReason | null;
  review_required_source: string | null;
  reviewed_at: string | null;
  reviewed_by_account_id: string | null;
  /** 派生: `review_required_at != null && reviewed_at == null`。 要確認表示に使う。 */
  review_required: boolean;
  /** `source_customer_problem_id` の表示投影 (課題名 / AIスコア / 確認状態)。
   * 一覧応答でのみ供給される (86eya0dva PR3)。参照はあるが課題が削除済みの場合や
   * mutation 応答では載らない。表示はこれを正とし、画面ターゲット単位の
   * customer_problems 取得へ依存しない (混在行で欠落するため)。 */
  source_problem?: SalesActionSourceProblem | null;
  /**
   * 受注後キュー (U2・ADR 20260812-contract-party-separation・2026-08-25 の U2
   * 実装時裁定): `linked_contract_id` が解決できたときの、リンク先契約の party
   * 表示投影。`is_individual_display` は server 側で導出済みの OR 合成結果
   * (OR 合成そのものの裁定は 2026-08-12) — frontend はこれをそのまま読み、
   * 判定を複製しない。一覧応答でのみ供給され、契約未作成行
   * (`linked_contract_id = null`) には無い。
   */
  contract_party?: SalesActionContractParty | null;
  created_at: string;
  updated_at: string;
}

export interface SalesActionContractParty {
  party_kind: 'firm' | 'individual' | null;
  individual_lawyer_id: string | null;
  individual_lawyer_name: string | null;
  is_individual_display: boolean;
}

export interface SalesActionSourceProblem {
  problem_name: string | null;
  /** 0-100 (backend で ROUND(confidence * 100) 済み)。 */
  ai_score: number | null;
  verification_status: 'unconfirmed' | 'confirmed' | 'denied' | null;
}

export interface PagedSalesActions {
  items: SalesAction[];
  total: number;
  page: number;
  page_size: number;
}

export interface SalesActionListQuery {
  target_kind?: 'firm' | 'lawyer';
  firm_id?: string;
  lawyer_id?: string;
  product_id?: string;
  sales_status?: SalesStatus;
  action_status?: ActionStatus;
  sort_by?: 'due_date' | 'updated_at' | 'created_at';
  sort_dir?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
}

export interface SalesActionCreateRequest {
  target: SalesActionTarget;
  /** 同席者 / 商談相手 (multi / catalog No.116)。 省略時は同席者なし。 */
  participant_lawyer_ids?: string[];
  product_id?: string | null;
  /** 商談前の候補ソリューション群。 省略可。 商談以降作成では UI が省略 (案件は product_id)。 */
  candidate_product_ids?: string[];
  sales_status?: SalesStatus;
  action_type?: ActionType | null;
  action_title?: string | null;
  customer_action?: string | null;
  success_condition?: string | null;
  note?: string | null;
  likelihood?: string | null;
  due_date?: string | null;
  lost_reason?: LostReason | null;
  lost_reason_note?: string | null;
  source_customer_problem_id?: string | null;
}

/// Generic PATCH payload. Lifecycle columns are locked server-side and are
/// not exposed here. Use the `/complete` and `DELETE` endpoints to move
/// the lifecycle.
export interface SalesActionPatchRequest {
  /** 同席者 / 商談相手 (multi / catalog No.116)。 省略時は変更なし、 `[]` で全クリア (置換)。 */
  participant_lawyer_ids?: string[];
  /** 商談前の候補ソリューション。 省略時は変更なし、 `[]` で全クリア (置換)。 */
  candidate_product_ids?: string[];
  /** 受注後に作成した契約への紐付け。 省略時は変更なし、 null で解除。 */
  linked_contract_id?: string | null;
  action_type?: ActionType | null;
  action_title?: string | null;
  customer_action?: string | null;
  success_condition?: string | null;
  note?: string | null;
  likelihood?: string | null;
  due_date?: string | null;
  source_customer_problem_id?: string | null;
}

export interface SalesActionCompleteRequest {
  completion_result: CompletionResult;
  completion_note?: string | null;
  next_sales_status?: SalesStatus | null;
  /** Omitted = keep current solution, null = clear, string = set. */
  product_id?: string | null;
  lost_reason?: LostReason | null;
  lost_reason_note?: string | null;
}

/// Inline status update payload for PATCH /api/sales-actions/{id}/status.
/// Non-terminal statuses keep action_status=open. won/lost terminalize the
/// row as completed; lost requires product_id, lost_reason, and
/// lost_reason_note server-side. product_id omitted keeps the current
/// solution, null clears it, and a string sets it.
export interface SalesActionStatusUpdateRequest {
  sales_status: SalesStatus;
  product_id?: string | null;
  lost_reason?: LostReason | null;
  lost_reason_note?: string | null;
}

/// 「ソリューションを追加」 (POST /api/sales-actions/{id}/product-clones)。
/// 選択ソリューションごとに同じ商談条件の新しい商談行を作る。 copy_note=true のときだけ
/// メモをコピーする (成果・契約・履歴はコピーしない)。
export interface SalesActionProductClonesRequest {
  product_ids: string[];
  copy_note?: boolean;
}

export interface SalesActionProductClonesResponse {
  created: Array<{ id: string }>;
  /// reason: 'already_open' | 'same_as_source' | 'duplicate_request'
  skipped: Array<{ product_id: string; reason: string }>;
}
